import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FreelancerUserService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching the freelancer user list.
 *
 * @param {Object} params - Query parameters (page, limit, search, application_status, verification_status, service_status, profession_id, date_from, date_to)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useListFreelancerUser = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["freelancer-user-list", params],
    queryFn: () => {
      return FreelancerUserService.getList(params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Custom hook for fetching a single freelancer user's detail.
 *
 * @param {string} uuid - Freelancer profile UUID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useGetFreelancerUser = (uuid, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["freelancer-user", uuid],
    queryFn: () => {
      return FreelancerUserService.get(uuid);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid,
  });
};

/**
 * Custom hook for approving a freelancer's application.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useApproveApplication = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => {
      return FreelancerUserService.approveApplication(uuid);
    },
    onSuccess: (data, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["freelancer-user-list"] });
      queryClient.invalidateQueries({ queryKey: ["freelancer-user", uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for rejecting a freelancer's application with a reason.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useRejectApplication = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, reason }) => {
      return FreelancerUserService.rejectApplication(uuid, reason);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["freelancer-user-list"] });
      queryClient.invalidateQueries({ queryKey: ["freelancer-user", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for approving a freelancer's uploaded verification documents.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useApproveDocuments = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => {
      return FreelancerUserService.approveDocuments(uuid);
    },
    onSuccess: (data, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["freelancer-user-list"] });
      queryClient.invalidateQueries({ queryKey: ["freelancer-user", uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for rejecting a freelancer's uploaded verification documents with a reason.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useRejectDocuments = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, reason }) => {
      return FreelancerUserService.rejectDocuments(uuid, reason);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["freelancer-user-list"] });
      queryClient.invalidateQueries({ queryKey: ["freelancer-user", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for approving one of a freelancer's individual services.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useApproveService = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => {
      return FreelancerUserService.approveService(uuid);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["freelancer-user"] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for rejecting one of a freelancer's individual services.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useRejectService = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => {
      return FreelancerUserService.rejectService(uuid);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["freelancer-user"] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for granting a bulk "premium reward" (free months of premium) to freelancer users.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useGrantPremiumReward = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (uuids) => {
      return FreelancerUserService.grantPremiumReward(uuids);
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Premium reward granted successfully");
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 0, // Don't auto-retry a bulk grant - avoids double-granting on transient failure
  });
};

export {
  useListFreelancerUser,
  useGetFreelancerUser,
  useApproveApplication,
  useRejectApplication,
  useApproveDocuments,
  useRejectDocuments,
  useApproveService,
  useRejectService,
  useGrantPremiumReward,
};
